class RateLimiter {
  private maxCalls: number;
  private timeWindow: number;
  private calls: number[];
  private totalCalls: number;

  constructor(maxCalls = 1, timeWindow = 1000) {
    this.maxCalls = maxCalls;
    this.timeWindow = timeWindow;
    this.calls = [];
    this.totalCalls = 0;
  }

  async throttle() {
    const now = Date.now();
    this.calls = this.calls.filter((time) => now - time < this.timeWindow);

    if (this.calls.length >= this.maxCalls) {
      const oldestCall = this.calls[0];
      const waitTime = this.timeWindow - (now - oldestCall);
      if (waitTime > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, waitTime));
      }
    }

    this.calls.push(Date.now());
  }

  incrementTotalCalls() {
    this.totalCalls += 1;
  }

  getStats() {
    const now = Date.now();
    const recentCalls = this.calls.filter((time) => now - time < this.timeWindow);
    return {
      totalCalls: this.totalCalls,
      recentCalls: recentCalls.length,
      isProcessing: false,
    };
  }
}

export const zerodhaRateLimiter = new RateLimiter(1, 1500);

export const rateLimitedApiCall = async <T>(apiCall: () => Promise<T>): Promise<T> => {
  await zerodhaRateLimiter.throttle();
  zerodhaRateLimiter.incrementTotalCalls();

  try {
    const result = await apiCall();
    return result;
  } catch (error: any) {
    if (error?.status === 429 || (error?.message && error.message.includes("Too many requests"))) {
      await new Promise<void>((resolve) => setTimeout(resolve, 2000));

      try {
        const retryResult = await apiCall();
        return retryResult;
      } catch (retryError) {
        throw retryError;
      }
    }

    throw error;
  }
};

export const getApiStats = () => {
  return zerodhaRateLimiter.getStats();
}; 